import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import slugify from 'slugify';
import Layout from '~components/Layout';
import Topper from '~components/base/Topper';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import Byline from '~components/base/Bylines';
import useBusiness from '~lib/useBusiness';
import GlobalStyles from '../styles/GlobalStyles';

export const pageQuery = graphql`
    query {
        site {
          siteMetadata {
            title
            sectionName
            projectName
          }
        }
        story: arcStory(storyId: {eq: "KCOCG5MD3NC7ZH27SOR7VGJSCA"}) {
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            credits {
                name
                url
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              level
              srcSet
              caption
              subtype
              bodyElements {
                _id
                content
              }
              citation {
                content
              }
            }
            # getting seo social data
            promoImage {
              url
              srcSet
              caption
              credit {
                name
              }
            }
            description {
              basic
            }
            # getting teasers information
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const ArticleTemplate = ({
  data: {
    story,
    site: {
      pathPrefix,
      siteMetadata: { sectionName, projectName },
    },
  },
}) => {
  const slugifiedSection = slugify(sectionName, { lower: true });
  const authorNames = get(story, 'credits', [])
    .map((credit) => credit.name)
    .join(', ');

  const [updateDate, setUpdateDate] = useState(null);

  useBusiness({
    pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
    environment: process.env.NODE_ENV,
    meterContent: (process.env.NODE_ENV !== 'development'),
    pageId: `apps.${story.slug}`,
    section: slugifiedSection,
    author: authorNames,
    projectName,
  });

  const subhed = get(story, 'subheadlines.basic')
    ? (<h3 className="subheadline miller-n4">{get(story, 'subheadlines.basic')}</h3>)
    : (null);
  const metaUrl = `https://apps.bostonglobe.com${pathPrefix}`;

  // testing for presence of publishDate archie element
  // if it exists, set the pubDateObj to it's value and pass to the Byline component below
  // if it doesn't pass null
  let pubDateObj = null;
  story.bodyElements.forEach((e) => {
    if (e.archieJson) {
      const parsedArchie = JSON.parse(e.archieJson);
      if (parsedArchie.publishDate !== undefined) {
        pubDateObj = parsedArchie;
      }
    }
  });

  return (
    <Layout>
      <GlobalStyles />
      <Topper>
        <h1 className="headline millerbanner-n4">{get(story, 'headlines.basic')}</h1>
        {subhed}
        <Byline
          data={get(story, 'credits')}
          pubDate={pubDateObj}
          updateDate={updateDate}
        />
      </Topper>
      <main>
        {story.bodyElements.map((element) => {
          if (element.archieJson) {
            return (
              <ArchieElement
                data={element}
                key={element._id}
                relatedLinks={get(story, 'relatedLinks')}
                setUpdateDate={setUpdateDate}
              />
            );
          }
          return (
            <BodyElement
              data={element}
              key={element._id}
            />
          );
        })}
      </main>
    </Layout>
  );
};

ArticleTemplate.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
        }),
      ),
      slug: PropTypes.string,
    }),
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default ArticleTemplate;
